import React from 'react';
import { Box, Typography } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SmsIcon from '@mui/icons-material/Sms';
import { WhatsappIconOutline } from '../CustomSVGIcons/WhatsappIconOutline';
import { ThspianFullLogo } from '../ThspianLogo/ThspianFullLogo';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import {
    ensureObsidianDashboardFonts,
    OBSIDIAN,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
} from '../../constants/obsidianDashboardTheme';
import { audme_app_icon } from '../../asset/images';

export type OtpChannel = 'sms' | 'whatsapp' | 'email' | 'audme';

export interface OtpPreferenceOption {
    channel: OtpChannel;
    viaLabel: string;
    title: string;
    icon: React.ReactNode;
    audmeSetupHint?: React.ReactNode;
}

interface OtpPreferenceSecurityLayoutProps {
    title: string;
    subtitle: string;
    stepIndex: number;
    totalSteps: number;
    options: OtpPreferenceOption[];
    selected: OtpChannel | '';
    onSelect: (channel: OtpChannel) => void;
}

function OtpPreferenceOptionCard({
    option,
    selected,
    onSelect,
}: {
    option: OtpPreferenceOption;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <Box sx={{ width: '100%' }}>
            <Box
                component="button"
                type="button"
                onClick={onSelect}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    p: { xs: 1.75, sm: 2.5 },
                    borderRadius: '9999px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: selected ? `1px solid rgba(255, 201, 101, 0.4)` : '1px solid transparent',
                    bgcolor: OBSIDIAN.surfaceContainerLow,
                    boxShadow: selected ? '0 0 20px rgba(255, 201, 101, 0.15)' : 'none',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                        borderColor: 'rgba(255, 201, 101, 0.2)',
                    },
                    '&:active': {
                        transform: 'scale(0.98)',
                    },
                }}>
                <Box
                    sx={{
                        width: { xs: 44, sm: 56 },
                        height: { xs: 44, sm: 56 },
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: selected ? 'rgba(255, 201, 101, 0.1)' : OBSIDIAN.surfaceContainerHighest,
                        mr: { xs: 1.5, sm: 2.5 },
                        transition: 'background-color 0.25s ease',
                    }}>
                    {option.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                            mb: 0.25,
                            fontFamily: OBSIDIAN_BODY_FONT,
                        }}>
                        {option.viaLabel}
                    </Typography>
                    <Typography
                        noWrap
                        sx={{
                            display: 'block',
                            fontSize: { xs: 16, sm: 18 },
                            fontWeight: 600,
                            color: OBSIDIAN.onSurface,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                        }}>
                        {option.title}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        borderRadius: '50%',
                        border: `2px solid ${selected ? OBSIDIAN.primary : OBSIDIAN.outline}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'border-color 0.2s ease',
                    }}>
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: OBSIDIAN.primary,
                            transform: selected ? 'scale(1)' : 'scale(0)',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </Box>
            </Box>
            {selected && option.audmeSetupHint ? <Box sx={{ mt: 1, ml: 2 }}>{option.audmeSetupHint}</Box> : null}
        </Box>
    );
}

export function OtpPreferenceSecurityLayout({
    title,
    subtitle,
    stepIndex,
    totalSteps,
    options,
    selected,
    onSelect,
}: OtpPreferenceSecurityLayoutProps) {
    const { themeStyle } = useThemeContext();
    ensureObsidianDashboardFonts();

    const stepNumber = Math.min(Math.max(stepIndex + 1, 1), totalSteps);
    const progressPct = totalSteps > 0 ? (stepNumber / totalSteps) * 100 : 0;

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                }}>
                <ThspianFullLogo width="140px" height="40px" fill={theme[themeStyle].logo_color ?? OBSIDIAN.primary} />
            </Box>

            <Box sx={{ mb: 4, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: OBSIDIAN.primary,
                        mb: 1,
                    }}>
                    Step {stepNumber} of {totalSteps}
                </Typography>
                <Box
                    sx={{
                        height: 4,
                        width: '100%',
                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                        borderRadius: '9999px',
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            height: '100%',
                            width: `${progressPct}%`,
                            bgcolor: OBSIDIAN.primary,
                            borderRadius: '9999px',
                            transition: 'width 0.35s ease',
                        }}
                    />
                </Box>
            </Box>

            <Box sx={{ mb: 4, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontSize: { xs: 24, sm: 32 },
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        color: OBSIDIAN.onSurface,
                        mb: 1,
                        lineHeight: 1.2,
                        overflowWrap: 'anywhere',
                    }}>
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize: { xs: 15, sm: 18 },
                        lineHeight: 1.55,
                        color: OBSIDIAN.onSurfaceVariant,
                        overflowWrap: 'anywhere',
                    }}>
                    {subtitle}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                {options.map(option => (
                    <OtpPreferenceOptionCard
                        key={option.channel}
                        option={option}
                        selected={selected === option.channel}
                        onSelect={() => onSelect(option.channel)}
                    />
                ))}
            </Box>
        </Box>
    );
}

export function buildOtpChannelIcon(channel: OtpChannel, selected: boolean): React.ReactNode {
    const color = selected ? OBSIDIAN.primary : OBSIDIAN.onSurfaceVariant;
    const sx = { fontSize: 28, color };

    switch (channel) {
        case 'audme':
            return <Box component="img" src={audme_app_icon} alt="" sx={{ width: 28, height: 28, display: 'block' }} />;
        case 'email':
            return <AlternateEmailIcon sx={sx} />;
        case 'whatsapp':
            return <WhatsappIconOutline style={{ width: 28, height: 28, color }} />;
        case 'sms':
            return <SmsIcon sx={sx} />;
        default:
            return <SecurityIcon sx={sx} />;
    }
}
